import type { CutCandidate } from "@veyso/core";

export type { CutCandidate };

/**
 * Deciding which parts of a recording are worth keeping.
 *
 * A screen demo recorded in one take is mostly dead air: waiting for a page to
 * load, reading, hunting for a menu, saying "um, let me just". None of it is
 * wrong, and all of it is boring. Cutting it is the single biggest difference
 * between a recording and a video.
 *
 * The rule is deliberately conservative — keep anything near speech or near an
 * interaction — because a cut that removes something the viewer needed is far
 * worse than a few seconds of slack that survived.
 */

export interface TimeRange {
  start: number;
  end: number;
}

export interface CutOptions {
  /** Dead stretches shorter than this are left alone; cutting them looks twitchy. */
  minGapSeconds: number;
  /** Kept either side of anything interesting, so cuts never clip a word. */
  handleSeconds: number;
  /** A kept range shorter than this is a flash frame, not a shot. */
  minKeepSeconds: number;
  /** Kept after an interaction: the result of a click is the interesting part. */
  afterActionSeconds: number;
}

export const defaultCutOptions: CutOptions = {
  minGapSeconds: 2,
  handleSeconds: 0.4,
  minKeepSeconds: 0.8,
  afterActionSeconds: 1.6,
};

export interface CutPlanInput {
  durationSeconds: number;
  /** Transcript segments — where the speaker is actually saying something. */
  speech?: readonly TimeRange[];
  /** Interaction timestamps from the recorder. */
  actions?: readonly number[];
  options?: Partial<CutOptions>;
}

export interface CutPlan {
  /** Ranges of the original to keep, in order, never overlapping. */
  keeps: TimeRange[];
  keptSeconds: number;
  removedSeconds: number;
}

function mergeRanges(ranges: TimeRange[]): TimeRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: TimeRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) last.end = Math.max(last.end, range.end);
    else merged.push({ ...range });
  }
  return merged;
}

/**
 * Everything interesting, expanded by a handle and merged.
 *
 * The one definition of "something is happening here", shared by the two
 * planners so they cannot disagree about it.
 */
function interestingRanges(input: CutPlanInput, options: CutOptions, duration: number): TimeRange[] {
  const interesting: TimeRange[] = [];
  for (const segment of input.speech ?? []) {
    interesting.push({
      start: Math.max(0, segment.start - options.handleSeconds),
      end: Math.min(duration, segment.end + options.handleSeconds),
    });
  }
  for (const at of input.actions ?? []) {
    interesting.push({
      start: Math.max(0, at - options.handleSeconds),
      end: Math.min(duration, at + options.afterActionSeconds),
    });
  }
  return mergeRanges(interesting).filter((range) => range.end > range.start);
}

/**
 * The stretches a cut could remove, worked out rather than proposed.
 *
 * This is the fix for the failure that started all of this: asked for free-form
 * ranges, a model named the wrong side of every boundary and removed twelve
 * seconds of narration while leaving a twenty-five second silence untouched.
 * Nothing checked it, because the review step protects clicks and deliberately
 * does not protect speech.
 *
 * So the arithmetic decides what *could* go and the model only decides which of
 * those to take. A candidate contains no speech and no interaction by
 * construction, which makes "cut the wrong side" unreachable rather than
 * merely unlikely — and it turns an open question into a short multiple choice,
 * which is a far easier thing for a model reading a transcript to get right.
 */
export function findCutCandidates(input: CutPlanInput): CutCandidate[] {
  const options = { ...defaultCutOptions, ...input.options };
  const duration = Math.max(0, input.durationSeconds);
  if (duration <= 0) return [];

  const merged = interestingRanges(input, options, duration);
  // Nothing to go on. Offering the whole recording as removable is not a
  // conservative default, it is the opposite of one.
  if (merged.length === 0) return [];

  const gaps = invertRanges(merged, duration).filter((range) => range.end - range.start >= options.minGapSeconds);

  return gaps.map((range, index) => ({
    id: `gap_${String(index + 1).padStart(2, "0")}`,
    start: Number(range.start.toFixed(3)),
    end: Number(range.end.toFixed(3)),
    duration: Number((range.end - range.start).toFixed(3)),
  }));
}

/**
 * Applies the candidates that were chosen, and only those.
 *
 * There is no review pass here and none is needed: unlike a free-form proposal,
 * a candidate was built from the recording's own timings, so there is nothing
 * to check it against that it was not derived from. An id that was never
 * offered is dropped rather than guessed at.
 */
export function planCutsFromCandidates(input: {
  durationSeconds: number;
  candidates: readonly CutCandidate[];
  /** Ids of the candidates to remove. Anything unrecognised is ignored. */
  removeIds: readonly string[];
  /** Stretches the user has pinned. These outrank the choice completely. */
  pinned?: readonly TimeRange[];
  options?: Partial<CutOptions>;
}): CutPlan {
  const options = { ...defaultCutOptions, ...input.options };
  const duration = Math.max(0, input.durationSeconds);
  if (duration <= 0) return { keeps: [], keptSeconds: 0, removedSeconds: 0 };

  const wanted = new Set(input.removeIds);
  const chosen = input.candidates.filter((candidate) => wanted.has(candidate.id));

  const pinnedMerged = mergeRanges(
    (input.pinned ?? [])
      .filter((range) => range.end > range.start)
      .map((range) => ({ start: Math.max(0, range.start), end: Math.min(duration, range.end) })),
  );

  let drops: TimeRange[] = [];
  for (const candidate of chosen) {
    drops.push(...subtractRanges({ start: candidate.start, end: candidate.end }, pinnedMerged));
  }
  drops = mergeRanges(drops).filter((range) => range.end - range.start >= options.minGapSeconds);

  const keeps = invertRanges(drops, duration).filter((range) => range.end - range.start >= options.minKeepSeconds);
  const keptSeconds = keeps.reduce((total, range) => total + (range.end - range.start), 0);

  // Same backstop as the proposal planner: a plan that leaves no video is not a
  // plan, whatever it removed.
  if (keeps.length === 0 || keptSeconds < MIN_SURVIVING_SECONDS) {
    return { keeps: [{ start: 0, end: duration }], keptSeconds: duration, removedSeconds: 0 };
  }

  return {
    keeps,
    keptSeconds: Number(keptSeconds.toFixed(3)),
    removedSeconds: Number(Math.max(0, duration - keptSeconds).toFixed(3)),
  };
}

/**
 * Works out what to keep.
 *
 * Everything interesting is expanded by a handle and merged; the gaps that
 * remain are the candidates for removal. A gap is only cut if it is long enough
 * to be worth it — otherwise the two neighbours are joined, because a cut that
 * saves half a second costs more in jitter than it gains.
 */
export function planCuts(input: CutPlanInput): CutPlan {
  const options = { ...defaultCutOptions, ...input.options };
  const duration = Math.max(0, input.durationSeconds);
  if (duration <= 0) return { keeps: [], keptSeconds: 0, removedSeconds: 0 };

  const merged = interestingRanges(input, options, duration);

  // Nothing to go on — a silent recording with no captured interactions. Keeping
  // all of it is the only honest answer; guessing from pixels alone would cut
  // things the user cares about.
  if (merged.length === 0) {
    return { keeps: [{ start: 0, end: duration }], keptSeconds: duration, removedSeconds: 0 };
  }

  // Join neighbours separated by less than the minimum gap: the join is cheaper
  // than the cut would be.
  const joined: TimeRange[] = [];
  for (const range of merged) {
    const last = joined[joined.length - 1];
    if (last && range.start - last.end < options.minGapSeconds) last.end = Math.max(last.end, range.end);
    else joined.push({ ...range });
  }

  const keeps = joined.filter((range) => range.end - range.start >= options.minKeepSeconds);
  if (keeps.length === 0) {
    return { keeps: [{ start: 0, end: duration }], keptSeconds: duration, removedSeconds: 0 };
  }

  const keptSeconds = keeps.reduce((total, range) => total + (range.end - range.start), 0);
  return {
    keeps,
    keptSeconds: Number(keptSeconds.toFixed(3)),
    removedSeconds: Number((duration - keptSeconds).toFixed(3)),
  };
}

/**
 * Where a moment in the original lands once the cuts are applied.
 *
 * Returns null for a moment inside a removed stretch — the caller decides
 * whether to drop it or clamp it, and those are genuinely different: an action
 * in a cut region should disappear, while a caption straddling a cut should
 * survive at the boundary.
 */
export function mapThroughCuts(seconds: number, keeps: readonly TimeRange[]): number | null {
  let elapsed = 0;
  for (const range of keeps) {
    if (seconds < range.start) return null;
    if (seconds <= range.end) return Number((elapsed + (seconds - range.start)).toFixed(3));
    elapsed += range.end - range.start;
  }
  return null;
}

/**
 * Whether anything of a stretch survives the cuts.
 *
 * The third answer the other two could not give. `mapThroughCuts` reports where
 * a single moment lands and `clampThroughCuts` snaps it to an edge, but a scene
 * spans a range, and the two cases look identical to both: a line spoken across
 * a boundary still has footage behind part of it, while a line whose material
 * was removed outright has none.
 *
 * Clamping the second kind kept it at near-zero length on the cut boundary,
 * still carrying its words and its voice clip, so narration bunched at cut
 * points describing footage that was gone.
 */
export function survivesCuts(start: number, end: number, keeps: readonly TimeRange[]): boolean {
  if (keeps.length === 0) return true;
  // Any overlap at all is enough. A scene needs only some of its material to
  // still be on screen to be worth keeping.
  return keeps.some((range) => start <= range.end && end >= range.start);
}

/** As `mapThroughCuts`, but snaps a moment in a removed stretch to the nearest edge. */
export function clampThroughCuts(seconds: number, keeps: readonly TimeRange[]): number {
  if (keeps.length === 0) return seconds;

  let elapsed = 0;
  for (const range of keeps) {
    if (seconds < range.start) return Number(elapsed.toFixed(3));
    if (seconds <= range.end) return Number((elapsed + (seconds - range.start)).toFixed(3));
    elapsed += range.end - range.start;
  }
  return Number(elapsed.toFixed(3));
}

/** Below this there is no video left to speak of, whatever the plan says. */
const MIN_SURVIVING_SECONDS = 3;

/** What is left of `range` once every one of `holes` is taken out of it. */
function subtractRanges(range: TimeRange, holes: readonly TimeRange[]): TimeRange[] {
  let pieces: TimeRange[] = [range];
  for (const hole of holes) {
    const next: TimeRange[] = [];
    for (const piece of pieces) {
      if (hole.end <= piece.start || hole.start >= piece.end) {
        next.push(piece);
        continue;
      }
      if (hole.start > piece.start) next.push({ start: piece.start, end: hole.start });
      if (hole.end < piece.end) next.push({ start: hole.end, end: piece.end });
    }
    pieces = next;
  }
  return pieces.filter((piece) => piece.end > piece.start);
}

/** The complement of `holes` within `[0, duration]`. */
function invertRanges(holes: readonly TimeRange[], duration: number): TimeRange[] {
  const keeps: TimeRange[] = [];
  let cursor = 0;
  for (const hole of holes) {
    if (hole.start > cursor) keeps.push({ start: cursor, end: hole.start });
    cursor = Math.max(cursor, hole.end);
  }
  if (cursor < duration) keeps.push({ start: cursor, end: duration });
  return keeps;
}

/** A scene the script wrote, and how long its line takes to say. */
export interface NarratedScene {
  sourceStart: number;
  sourceEnd: number;
  /** Seconds of speech: measured from the rendered clip, or estimated from the words. */
  narrationSeconds: number;
}

/**
 * Air left after a line finishes, so the picture does not cut on the last
 * syllable. Small — this is breathing room, not a pause.
 */
const NARRATION_TAIL_SECONDS = 0.9;

/**
 * Cuts a recording down to the length of its own narration.
 *
 * The silence-based planner cannot help a recording nobody spoke over: it finds
 * dead air by looking for gaps between words, and with no words the whole take
 * is one gap. Pointed at such a recording it removed almost all of it, keeping
 * only the fragments where the transcriber had hallucinated.
 *
 * What those projects do have is a script written from the picture. Each scene
 * names the stretch it describes and carries a line that takes a known time to
 * read, and that is enough: keep, from the front of each stretch, about as much
 * footage as its line takes to speak. The video then runs at the pace of the
 * voice instead of playing two minutes of screen under eleven seconds of talk.
 *
 * Anchored at the start of each scene rather than centred on it, because the
 * line describes what happens when the scene begins — that is the frame it has
 * to be heard over, and it is what makes the narration land in step once the
 * cut is applied.
 */
export function fitCutsToNarration(
  scenes: readonly NarratedScene[],
  durationSeconds: number,
  options: Pick<CutOptions, "minKeepSeconds"> = defaultCutOptions,
): CutPlan {
  const keeps = mergeRanges(
    scenes
      .filter((scene) => scene.sourceEnd > scene.sourceStart)
      .map((scene) => {
        const start = Math.max(0, Math.min(scene.sourceStart, durationSeconds));
        const available = Math.min(scene.sourceEnd, durationSeconds) - start;
        // Never more of a scene than the scene has: a four-second stretch with a
        // thirty-second line keeps its four seconds rather than borrowing from
        // whatever happens to follow it.
        const wanted = Math.max(options.minKeepSeconds, scene.narrationSeconds + NARRATION_TAIL_SECONDS);
        return { start, end: start + Math.min(available, wanted) };
      })
      .filter((range) => range.end > range.start),
  );

  const keptSeconds = keeps.reduce((total, range) => total + (range.end - range.start), 0);
  return {
    keeps,
    keptSeconds: Number(keptSeconds.toFixed(3)),
    removedSeconds: Number(Math.max(0, durationSeconds - keptSeconds).toFixed(3)),
  };
}

/** A script scene the window chooser is deciding for. */
export interface WindowScene {
  id: string;
  sourceStart: number;
  sourceEnd: number;
  narrationSeconds: number;
}

/** One slice of a scene the narration could be played over. */
export interface WindowCandidate {
  id: string;
  start: number;
  end: number;
  /** Where this window came from — shown to the model and to the reviewer. */
  anchor: "scene-start" | "action" | "screen-change" | "most-relevant";
  /** The arithmetic answer. Always present, so declining to move is a choice. */
  isDefault: boolean;
}

/** More than this and the list stops being a choice and becomes a search. */
const MAX_WINDOW_CANDIDATES = 6;

/** Anchors closer together than this are the same moment. */
const ANCHOR_MERGE_SECONDS = 1.5;

/**
 * The slices of a scene its narration could reasonably play over.
 *
 * `fitCutsToNarration` keeps the front of every scene, which is how the cut
 * ended up anchored on page loads: a scene boundary is where the *script*
 * decided one idea ends, not where the thing it describes happens. The moment
 * the line is about usually sits somewhere inside the span, and two signals
 * already collected say where — the clicks the recorder captured, and the
 * stretches the vision pass described and scored.
 *
 * So the answer is offered rather than computed. Each candidate is a window of
 * the line's own length hung off a real event, clamped inside the scene; a model
 * picks one by id. It cannot name a range of its own — the same constraint the
 * cut prompt has carried since free-form timestamps produced cuts on the wrong
 * side of the thing they were meant to keep.
 */
export function narrationWindowCandidates(
  scene: WindowScene,
  signals: {
    actions?: readonly number[];
    visualScenes?: readonly { start: number; end: number; relevance?: number }[];
  },
  options: Pick<CutOptions, "minKeepSeconds"> = defaultCutOptions,
): WindowCandidate[] {
  const span = scene.sourceEnd - scene.sourceStart;
  const wanted = Math.max(options.minKeepSeconds, scene.narrationSeconds + NARRATION_TAIL_SECONDS);

  // Nothing to choose between: the scene is shorter than the line, so the whole
  // scene is the only window there is.
  if (span <= wanted) {
    return [
      {
        id: `${scene.id}:whole`,
        start: scene.sourceStart,
        end: scene.sourceEnd,
        anchor: "scene-start",
        isDefault: true,
      },
    ];
  }

  const inside = (at: number) => at > scene.sourceStart && at < scene.sourceEnd;
  const anchors: { at: number; anchor: WindowCandidate["anchor"] }[] = [
    { at: scene.sourceStart, anchor: "scene-start" },
    ...(signals.actions ?? []).filter(inside).map((at) => ({ at, anchor: "action" as const })),
    ...(signals.visualScenes ?? [])
      .map((visual) => visual.start)
      .filter(inside)
      .map((at) => ({ at, anchor: "screen-change" as const })),
  ];

  // What the vision pass thought mattered most in this span, which is a
  // different question from where the picture happened to change.
  const best = (signals.visualScenes ?? [])
    .filter((visual) => visual.start < scene.sourceEnd && visual.end > scene.sourceStart)
    .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))[0];
  if (best && inside(best.start)) anchors.push({ at: best.start, anchor: "most-relevant" });

  const candidates: WindowCandidate[] = [];
  for (const { at, anchor } of anchors.sort((a, b) => a.at - b.at)) {
    // The scene-start candidate is the default and is never merged away.
    if (candidates.some((existing) => Math.abs(existing.start - at) < ANCHOR_MERGE_SECONDS)) continue;

    // An anchor too near the end cannot hold a full window ahead of it, so the
    // window ends at the scene and starts far enough back to still fit. Better
    // a window that includes the moment than one cut short by it.
    const start = Math.min(at, scene.sourceEnd - wanted);
    candidates.push({
      id: `${scene.id}:${anchor === "scene-start" ? "start" : at.toFixed(1)}`,
      start: Number(start.toFixed(3)),
      end: Number((start + wanted).toFixed(3)),
      anchor,
      isDefault: anchor === "scene-start",
    });
    if (candidates.length >= MAX_WINDOW_CANDIDATES) break;
  }

  return candidates;
}

/**
 * The most a window may run beyond the line it carries.
 *
 * Latitude, not licence. A busy stretch deserves longer than a quiet one, which
 * the fixed `line + 0.9s` formula could never give it — but a window the length
 * of its scene is the model declining to cut, and fitting the footage to the
 * narration is the entire job.
 */
const MAX_WINDOW_STRETCH = 2.2;

/**
 * Validates a window the model sized itself.
 *
 * Free-form timestamps have gone badly in this codebase before, which is why
 * the cut prompt forbids them. The difference here is that the bounds are known
 * exactly — a window belongs to one scene, and must be long enough to carry
 * that scene's line — so an answer can be checked rather than trusted.
 *
 * Returns `null` for a range that cannot be repaired, and the caller falls back
 * to the arithmetic window. `adjusted` says the model's numbers were changed,
 * which the review screen shows: a window somebody had to correct is a
 * different fact from one that arrived usable.
 */
export function clampNarrationWindow(
  scene: WindowScene,
  start: number,
  end: number,
  options: Pick<CutOptions, "minKeepSeconds"> = defaultCutOptions,
): { start: number; end: number; adjusted: boolean } | null {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  // Overlapping the scene is a usable answer; sitting outside it altogether is
  // an answer about a different scene.
  if (end <= scene.sourceStart || start >= scene.sourceEnd) return null;

  const span = scene.sourceEnd - scene.sourceStart;
  const shortest = Math.min(span, Math.max(options.minKeepSeconds, scene.narrationSeconds));
  const longest = Math.min(span, Math.max(shortest, scene.narrationSeconds * MAX_WINDOW_STRETCH));

  let from = Math.max(scene.sourceStart, Math.min(start, scene.sourceEnd));
  const length = Math.min(longest, Math.max(shortest, end - start));
  // Pushed back rather than truncated: a window that has to move to fit still
  // carries the whole line, where one cut short stops before the voice does.
  if (from + length > scene.sourceEnd) from = scene.sourceEnd - length;

  const adjusted = Math.abs(from - start) > 0.05 || Math.abs(length - (end - start)) > 0.05;
  return { start: Number(from.toFixed(3)), end: Number((from + length).toFixed(3)), adjusted };
}

/**
 * Where a moment of the finished video sits in the original recording.
 *
 * The reverse of `mapThroughCuts`, and needed wherever something indexed by the
 * recording is drawn against the cut — the filmstrip above all, whose contact
 * sheet is laid out in source seconds while the timeline lane it fills is body
 * seconds. Without this the strip shows frames from the wrong moments, which
 * looks like a rendering fault rather than a mapping one.
 *
 * An empty list means nothing was cut, so body time is source time.
 */
export function sourceSecondsAt(bodySeconds: number, keeps: readonly TimeRange[]): number {
  if (keeps.length === 0) return Math.max(0, bodySeconds);

  let remaining = Math.max(0, bodySeconds);
  for (const keep of keeps) {
    const length = keep.end - keep.start;
    if (remaining < length) return keep.start + remaining;
    remaining -= length;
  }
  // Past the end of the video: the last frame, not a time that does not exist.
  return keeps[keeps.length - 1].end;
}
